import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import pdfWorkerUrl from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";

/**
 * In-app viewer for patient-history attachments (VAPT: stored XSS via history upload).
 *
 * Uploaded files are never opened as documents on the app's origin (no blob: tabs, no
 * document.write). PDFs are rasterised with pdf.js with eval and XFA disabled and without the
 * scripting sandbox, so embedded JavaScript never runs (a text layer over each page keeps the
 * PDF's text selectable and copyable); text files are rendered as text; images go in
 * an <img>. Every file can also be saved with "Download".
 */

const IMAGE_TYPES = { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png" };

// Image decoders (JBIG2/CCITT scans, JPEG 2000) that pdf.js loads by name at runtime; served
// unhashed by the pdfjs-wasm plugin in vite.config.js. Without it scanned PDFs render blank.
const PDFJS_WASM_URL = new URL(`${import.meta.env.BASE_URL}pdfjs/wasm/`, window.location.origin).href;

let pdfjsPromise = null;
const loadPdfJs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([
      import("pdfjs-dist/legacy/build/pdf.mjs"),
      import("pdfjs-dist/web/pdf_viewer.css"), // .textLayer rules; lazy so the main bundle doesn't carry it
    ]).then(([pdfjs]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      return pdfjs;
    });
  }
  return pdfjsPromise;
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1000001, // above Fluent UI layers (Clinical Note modal)
    background: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  panel: {
    width: "min(1000px, 94vw)",
    height: "min(760px, 90vh)",
    display: "flex",
    flexDirection: "column",
    background: "#1f1f1f",
    color: "#f3f3f3",
    borderRadius: 6,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderBottom: "1px solid #3a3a3a",
  },
  title: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 },
  button: {
    background: "#0f6cbd",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    padding: "6px 14px",
    cursor: "pointer",
  },
  closeButton: {
    background: "transparent",
    color: "#f3f3f3",
    border: "1px solid #5a5a5a",
    borderRadius: 4,
    padding: "6px 12px",
    cursor: "pointer",
  },
  body: { flex: 1, overflow: "auto", padding: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
  text: { margin: 0, width: "100%", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace" },
  message: { margin: "auto", textAlign: "center", color: "#d0d0d0" },
  page: { maxWidth: "100%", background: "#fff", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.4)" },
};

/**
 * Stops a drag-selection from jumping to "everything after this point" when the pointer leaves
 * the text spans (past the end of a line, into a margin). Without this the browser resolves the
 * empty space to the end of the whole text layer. Port of pdf.js's TextLayerBuilder handling: an
 * .endOfContent div (styled by pdf_viewer.css) is moved right after the selection's moving end, so
 * the space under the pointer belongs to it instead.
 *
 * `textLayers` maps each .textLayer div to its .endOfContent div; listeners end with `signal`.
 */
const bindTextSelection = (textLayers, signal) => {
  const reset = (end, textLayer) => {
    textLayer.append(end);
    end.style.width = "";
    end.style.height = "";
    textLayer.classList.remove("selecting");
  };
  let isPointerDown = false;
  let prevRange = null;

  document.addEventListener("pointerdown", () => (isPointerDown = true), { signal });
  document.addEventListener(
    "pointerup",
    () => {
      isPointerDown = false;
      textLayers.forEach(reset);
    },
    { signal }
  );
  window.addEventListener(
    "blur",
    () => {
      isPointerDown = false;
      textLayers.forEach(reset);
    },
    { signal }
  );
  document.addEventListener("keyup", () => !isPointerDown && textLayers.forEach(reset), { signal });

  document.addEventListener(
    "selectionchange",
    () => {
      const selection = document.getSelection();
      if (selection.rangeCount === 0) {
        textLayers.forEach(reset);
        return;
      }
      for (const [textLayer, end] of textLayers) {
        let active = false;
        for (let i = 0; i < selection.rangeCount && !active; i++) {
          active = selection.getRangeAt(i).intersectsNode(textLayer);
        }
        if (active) textLayer.classList.add("selecting");
        else reset(end, textLayer);
      }
      // Firefox handles this natively (the layer is -moz-user-select: none)
      const first = textLayers.keys().next().value;
      if (!first || getComputedStyle(first).getPropertyValue("-moz-user-select") === "none") return;

      const range = selection.getRangeAt(0);
      const modifyStart =
        prevRange &&
        (range.compareBoundaryPoints(Range.END_TO_END, prevRange) === 0 ||
          range.compareBoundaryPoints(Range.START_TO_END, prevRange) === 0);
      let anchor = modifyStart ? range.startContainer : range.endContainer;
      if (anchor.nodeType === Node.TEXT_NODE) anchor = anchor.parentNode;
      const textLayer = anchor.parentElement?.closest(".textLayer");
      const end = textLayers.get(textLayer);
      if (end) {
        end.style.width = textLayer.style.width;
        end.style.height = textLayer.style.height;
        anchor.parentElement.insertBefore(end, modifyStart ? anchor : anchor.nextSibling);
      }
      prevRange = range.cloneRange();
    },
    { signal }
  );
};

const PdfPages = ({ bytes }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;
    const container = containerRef.current;
    const textLayers = new Map();
    const selectionListeners = new AbortController();
    bindTextSelection(textLayers, selectionListeners.signal);

    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        loadingTask = pdfjs.getDocument({
          data: bytes.slice(), // pdf.js transfers (detaches) the buffer; keep ours for Download
          isEvalSupported: false,
          enableXfa: false,
          wasmUrl: PDFJS_WASM_URL,
        });
        const pdf = await loadingTask.promise;
        const targetWidth = Math.min(container.clientWidth || 900, 900);
        const ratio = window.devicePixelRatio || 1;

        for (let pageNumber = 1; pageNumber <= pdf.numPages && !cancelled; pageNumber++) {
          const page = await pdf.getPage(pageNumber);
          const scale = targetWidth / page.getViewport({ scale: 1 }).width;
          const cssViewport = page.getViewport({ scale });
          const viewport = page.getViewport({ scale: scale * ratio });

          // The canvas is only a picture of the page; the text layer on top holds the page's
          // text as transparent spans (textContent, never HTML) so it can be selected and copied.
          const pageDiv = document.createElement("div");
          Object.assign(pageDiv.style, styles.page, {
            maxWidth: "none", // fixed size: the text layer is laid out for exactly this width
            position: "relative",
            flexShrink: 0,
            width: `${cssViewport.width}px`,
            height: `${cssViewport.height}px`,
          });
          // pdf.js sizes the text layer from these (--scale-factor in 4.x, --total-scale-factor in 5+)
          pageDiv.style.setProperty("--scale-factor", String(scale));
          pageDiv.style.setProperty("--user-unit", "1");
          pageDiv.style.setProperty("--total-scale-factor", String(scale));

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          Object.assign(canvas.style, { display: "block", width: "100%", height: "100%" });
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;

          const textLayerDiv = document.createElement("div");
          textLayerDiv.className = "textLayer";
          pageDiv.append(canvas, textLayerDiv);
          await new pdfjs.TextLayer({
            textContentSource: page.streamTextContent(),
            container: textLayerDiv,
            viewport: cssViewport,
          }).render();
          const endOfContent = document.createElement("div");
          endOfContent.className = "endOfContent";
          textLayerDiv.append(endOfContent);
          textLayerDiv.addEventListener("mousedown", () => textLayerDiv.classList.add("selecting"));
          textLayers.set(textLayerDiv, endOfContent);
          if (!cancelled) container.appendChild(pageDiv);
        }
      } catch (e) {
        if (!cancelled) setError("This PDF could not be displayed. Use Download to open it.");
      }
    })();

    return () => {
      cancelled = true;
      selectionListeners.abort();
      textLayers.clear();
      if (loadingTask) loadingTask.destroy();
      container.replaceChildren();
    };
  }, [bytes]);

  return (
    <>
      {error && <p style={styles.message}>{error}</p>}
      <div ref={containerRef} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, width: "100%" }} />
    </>
  );
};

const ImagePreview = ({ bytes, extension }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: IMAGE_TYPES[extension] }));
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [bytes, extension]);
  return url ? <img src={url} alt="" style={{ maxWidth: "100%", objectFit: "contain" }} /> : null;
};

const HistoryDocViewer = ({ filename, extension, bytes, error, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const download = () => {
    // octet-stream + download attribute: always saved, never opened inline on our origin
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/octet-stream" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  let content;
  if (error) {
    content = <p style={styles.message}>{error}</p>;
  } else if (extension === "pdf") {
    content = <PdfPages bytes={bytes} />;
  } else if (extension === "txt") {
    content = <pre style={styles.text}>{new TextDecoder("utf-8").decode(bytes)}</pre>;
  } else if (IMAGE_TYPES[extension]) {
    content = <ImagePreview bytes={bytes} extension={extension} />;
  } else {
    content = <p style={styles.message}>Preview is not available for this file type. Use Download to open it.</p>;
  }

  return (
    <div style={styles.backdrop} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.panel} role="dialog" aria-modal="true" aria-label={filename || "Attachment"}>
        <div style={styles.header}>
          <span style={styles.title} title={filename}>{filename || "Attachment"}</span>
          {bytes && (
            <button type="button" style={styles.button} onClick={download}>
              Download
            </button>
          )}
          <button type="button" style={styles.closeButton} onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <div style={styles.body}>{content}</div>
      </div>
    </div>
  );
};

/** Mounts the viewer on top of the current page; resolves when it is closed. */
export const openHistoryDocViewer = ({ filename, extension, bytes, error }) =>
  new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const close = () => {
      root.unmount();
      host.remove();
      resolve();
    };
    root.render(
      <HistoryDocViewer filename={filename} extension={extension} bytes={bytes} error={error} onClose={close} />
    );
  });

export default HistoryDocViewer;
